/**
 * AuthBrandHeader.tsx — SK Platform
 * The shared auth-flow brand mark: a gradient accent strip pinned to the top of
 * the card, and the "SK" logo tile beside the gradient "SOFTKNACK" wordmark.
 * Rendered by every auth screen (signup, check-email, verify-email, resend-
 * verification, MFA, invite-accept, personal-info) so they all wear the same
 * header — a different wordmark on one screen reads as a different product.
 *
 * The parent card MUST set `overflow: 'hidden'` so the absolutely-positioned
 * accent bar clips to the card's rounded corners.
 *
 * The tagline pill is OFF by default (it used to read "AI PRODUCTS FOR YOUR
 * ENTIRE BUSINESS"). The prop is kept so a screen that wants a line under the
 * mark can pass one, but no caller does today.
 */
import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';

/** No pill unless a caller explicitly asks for one. */
const DEFAULT_TAGLINE: string | null = null;

interface Props {
  /** Tagline pill text. Pass null to omit the pill. */
  tagline?: string | null;
}

const AuthBrandHeader: React.FC<Props> = ({ tagline = DEFAULT_TAGLINE }) => {
  const { theme } = useTheme();
  const { colors } = theme;

  return (
    <>
      {/* Premium gradient accent strip */}
      <View
        style={[
          styles.accentBar,
          { backgroundColor: colors.ink },
          Platform.OS === 'web'
            ? ({ backgroundImage: `linear-gradient(90deg, ${colors.ink} 0%, ${colors.accent} 50%, ${colors.accentInk} 100%)` } as any)
            : {},
        ]}
      />

      {/* Wordmark — SK icon + PLATFORM */}
      <View style={styles.logoRow}>
        <View style={[styles.logoMark, { backgroundColor: colors.ink }]}>
          <Text style={[styles.logoText, { color: colors.white }]}>SK</Text>
        </View>
        <Text
          style={[
            styles.logoName,
            { color: colors.ink },
            Platform.OS === 'web'
              ? ({
                  backgroundImage: `linear-gradient(160deg, ${colors.ink} 0%, ${colors.ink2} 55%, ${colors.accentInk} 135%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                } as any)
              : {},
          ]}
        >
          SOFTKNACK
        </Text>
      </View>

      {tagline ? (
        <View style={[styles.taglinePill, { backgroundColor: colors.accentSoft, borderColor: colors.accentLine }]}>
          <Text style={[styles.tagline, { color: colors.accentInk }]}>{tagline}</Text>
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 4 },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
    gap: 12,
  },
  logoMark: {
    width: 42,
    height: 42,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 8px 20px -5px rgba(15,23,42,0.40), inset 0 1px 0 rgba(255,255,255,0.12)' } as any)
      : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.32, shadowRadius: 12, elevation: 6 }),
  },
  logoText: {
    fontWeight: '900',
    fontSize: 16,
    fontFamily: Platform.OS === 'web' ? 'IBM Plex Mono, monospace' : undefined,
    letterSpacing: 0.3,
  },
  logoName: {
    fontSize: 23,
    fontWeight: '800',
    letterSpacing: 3.2,
    // An explicit line box, so the wordmark centres against the 42px tile
    // predictably instead of depending on the platform's default line height.
    lineHeight: 26,
    ...(Platform.OS === 'web'
      ? ({
          // Letter-spacing is applied AFTER the last glyph too, so the text
          // node is ~1 space wider than the word looks. Left uncorrected, the
          // centred row sits visibly off to the left. paddingRight keeps the
          // gradient from clipping the final letter; the negative margin then
          // removes that trailing space from the layout box so the mark is
          // optically centred rather than mathematically centred.
          paddingRight: 3,
          marginRight: -3.2,
        } as any)
      : { marginRight: -3.2 }),
  },
  taglinePill: {
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 5,
    marginBottom: 20,
  },
  tagline: {
    fontSize: 10,
    fontWeight: '800',
    textAlign: 'center',
    letterSpacing: 1.6,
  },
});

export default AuthBrandHeader;
